// Shared helpers for pages that render live class data from the registration
// Google Sheet (via GET /api/classes on the Express server). Keeping this in
// one place means the Fall Schedule and Classes by Age pages stay in sync
// with whatever columns the sheet actually has.

const DAY_EXPANSION = {
  'M/W/F': ['Monday', 'Wednesday', 'Friday'],
  'T/Th': ['Tuesday', 'Thursday'],
};

export const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function expandDays(daysField) {
  const key = (daysField || '').trim();
  return DAY_EXPANSION[key] || [key].filter(Boolean);
}

export function parseTimeToMinutes(timeStr) {
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec((timeStr || '').trim());
  if (!match) return 0;
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const meridiem = match[3].toUpperCase();
  if (meridiem === 'PM' && hours !== 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;
  return hours * 60 + minutes;
}

export function flagList(flags) {
  return (flags || '')
    .split(';')
    .map((f) => f.trim())
    .filter(Boolean);
}

const FLAG_LABELS = {
  TRYOUT: 'Tryout Required',
  SKILLLEVEL: 'Skill Level',
  ADULT: 'Adult',
  '2CLASSES': '2 Classes Required',
  'EXCEL TEAM MEMBER': 'Team Members Only',
};

export function flagLabel(flag) {
  return FLAG_LABELS[flag] || flag.replace(/_/g, ' ');
}

const STATUS_LABELS = {
  OPEN: 'Open',
  FULL: 'Full',
  WAITLIST: 'Waitlist',
};

export function statusLabel(status) {
  return STATUS_LABELS[(status || '').toUpperCase()] || status;
}

// Which visual style a status badge gets. OPEN shows no badge at all.
// FULL and WAITLIST keep their existing look. Anything else typed into the
// status column — "2 spots remaining", "1 spot left", "Almost full", etc. —
// gets the "limited" purple treatment and displays verbatim, so she isn't
// locked into a fixed set of phrases.
export function statusBadgeKind(status) {
  const s = (status || '').trim().toUpperCase();
  if (!s || s === 'OPEN') return null;
  if (s === 'FULL') return 'full';
  if (s === 'WAITLIST') return 'waitlist';
  return 'limited';
}

export async function fetchClasses(session = 'fall') {
  let res = await fetch(`/api/classes?session=${session}`);
  // A separate local preview can be running beside the studio's existing
  // local server. In that case, use the already-running schedule API solely
  // for previewing; deployed visitors always use their own site's API.
  if (!res.ok && window.location.hostname === 'localhost' && window.location.port !== '5000') {
    res = await fetch(`http://localhost:5000/api/classes?session=${session}`);
  }
  if (!res.ok) throw new Error(`Class schedule request failed (${res.status})`);
  const data = await res.json();
  return data.map((c) => ({
    ...c,
    age_min: Number(c.age_min) || 0,
    age_max: Number(c.age_max) || 99,
    _startMin: parseTimeToMinutes(c.start_time),
    _days: expandDays(c.days),
  }));
}

export function groupByDay(classes) {
  const byDay = {};
  DAY_ORDER.forEach((d) => (byDay[d] = []));
  classes.forEach((c) => {
    c._days.forEach((d) => {
      if (!byDay[d]) byDay[d] = [];
      byDay[d].push(c);
    });
  });
  Object.keys(byDay).forEach((d) => byDay[d].sort((a, b) => a._startMin - b._startMin));
  return byDay;
}

export function formatTimeRange(cls) {
  return `${cls.start_time} – ${cls.end_time}`;
}

export function formatAgeRange(cls) {
  if (cls.age_min <= 1 && cls.age_max >= 99) return 'All Ages';
  if (cls.age_max >= 99) return `Ages ${cls.age_min}+`;
  return `Ages ${cls.age_min}–${cls.age_max}`;
}
